/**
 * Deterministic, seeded pseudo-randomness.
 *
 * Everything the app "invents" (candidate designs, colour picks, motif spacing)
 * flows through here so that the same brief + the same seed always yields the
 * same designs, on any device, forever. That is what makes a design shareable
 * by URL alone with no backend.
 *
 * xmur3 (string -> 32-bit seed) + mulberry32 (32-bit state PRNG). Both are
 * small, well-known, and have no dependencies.
 */

function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

function mulberry32(a: number): () => number {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface Rng {
  /** Float in [0, 1). */
  next(): number;
  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number;
  /** Uniform pick. Throws on an empty list rather than returning undefined. */
  pick<T>(items: readonly T[]): T;
  /** Fisher-Yates copy. Does not mutate the input. */
  shuffle<T>(items: readonly T[]): T[];
  /** True with probability p. */
  chance(p: number): boolean;
}

export function makeRng(seed: string): Rng {
  const seedFn = xmur3(seed);
  const rand = mulberry32(seedFn());

  const rng: Rng = {
    next: rand,
    int(min, max) {
      if (max < min) throw new Error(`rng.int: max ${max} < min ${min}`);
      return min + Math.floor(rand() * (max - min + 1));
    },
    pick(items) {
      if (items.length === 0) throw new Error('rng.pick: empty list');
      return items[Math.floor(rand() * items.length)]!;
    },
    shuffle(items) {
      const out = items.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const a = out[i]!;
        const b = out[j]!;
        out[i] = b;
        out[j] = a;
      }
      return out;
    },
    chance(p) {
      return rand() < p;
    },
  };
  return rng;
}

/**
 * A short, typo-tolerant, human-shareable seed.
 * Avoids vowels (so it cannot spell anything unfortunate) and avoids the
 * 0/O and 1/I/L look-alikes so it survives being read off a phone screen.
 */
const SEED_ALPHABET = '23456789BCDFGHJKMNPQRSTVWXYZ';

export function randomSeed(entropy: () => number = Math.random): string {
  let out = '';
  for (let i = 0; i < 6; i++) {
    out += SEED_ALPHABET[Math.floor(entropy() * SEED_ALPHABET.length)];
  }
  return out;
}

/** Normalise user-typed seeds so "abc 123" and "ABC123" agree. */
export function normalizeSeed(seed: string): string {
  return seed.trim().toUpperCase().replace(/\s+/g, '');
}
